import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, withHandlers, withState } from 'recompose';
import { reduxForm, Field, FieldArray, SubmissionError } from 'redux-form';
import { useDecision } from '@optimizely/react-sdk';
import { OPTIMIZELY_FLAGS } from 'config/optimizely';
import { UnitReservationFormSelector } from 'store/selectors/bookings';
import { getLocalPhoneNumberByCityState } from 'store/pageMeta/actions';
import {
	createValidator,
	required,
	email,
	phone as phoneValidation,
} from 'utils/form/validation';
import { Form, FormItem, FormGroup, TextInput } from 'components/core/Form';
import { ReduxCAPTCHA } from 'components/form';
import { Spinner } from 'components/core/Spinner';
import { Button } from 'components/core/Button';
import { Image } from 'components/core/Image';
import { Link } from 'components/core/Link';
import { Paragraph, Text } from 'components/core/Typography';

import warningIcon from 'assets/images/icons/warning-icon.svg';

import UnitReservationFeeDropDown from 'components/fees/UnitReservationFeeDropDown';
import GiftCardModalLink from 'components/giftcard/GiftCardModalLink/GiftCardModalLink';
import { reservationFormHandler } from 'utils/reservationFormHandler';
import UnitDropdownDatepicker from '../UnitDropDownDatepicker/UnitDropdownDatepicker';
import AlternateNamesContainer from './AlternateNamesContainer';

import './unitReservationForm.scss';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';
import { GiftCardBanner } from 'components/Banners/GiftCardBanner';

const validate = createValidator({
	firstName: [required],
	lastName: [required],
	email: [required, email],
	phone: [required, phoneValidation],
	moveInDate: [required],
});

const fetchLocalPhoneNumber = async (
	city,
	state,
	getLocalPhoneNumberByCityState,
	setLocalPhone
) => {
	try {
		const phone = await getLocalPhoneNumberByCityState(city, state);
		const { number, unformattedNumber } = phone?.payload;
		try {
			setLocalPhone({
				number: number || '1-800-555-5555',
				unformattedNumber: unformattedNumber || '18005555555',
			});
		} catch (e) {
			// Ignore errors in test environments where DOM might not be available
		}
	} catch (error) {
		try {
			setLocalPhone({
				number: '1-800-555-5555',
				unformattedNumber: '18005555555',
			});
		} catch (e) {
			// Ignore errors in test environments where DOM might not be available
		}
		console.error(error);
	}
};

export const enhance = compose(
	connect(UnitReservationFormSelector, { getLocalPhoneNumberByCityState }),
	withState('botErrors', 'setBotErrors', false),
	withState('lastSubmission', 'setLastSubmission', null),
	withState('localPhone', 'setLocalPhone', null),
	withHandlers({
		onSubmit:
			(props) =>
			({ alternateNames = [], ...formData }) => {
				const {
					location: { city, state },
				} = props.facility;
				const noChangeSinceLastSubmission =
					props.lastSubmission &&
					JSON.stringify(props.lastSubmission) ===
						JSON.stringify(formData);
				if (noChangeSinceLastSubmission) {
					if (!props.localPhone) {
						fetchLocalPhoneNumber(
							city,
							state,
							props.getLocalPhoneNumberByCityState,
							props.setLocalPhone
						);
					}
					throw new SubmissionError({ _error: 'ReSubmissionError' });
				} else {
					props.setLastSubmission(formData);
					const bookingDetails = {
						...formData,
						email: formData.email?.toLowerCase(),
						facilityId: props.facility.id,
						unitId: props.unit.id,
						alternateFirstNames: alternateNames.map(
							(name) => name.firstName
						),
						alternateLastNames: alternateNames.map(
							(name) => name.lastName
						),
					};

					let click_url = props?.unit?.url || null;

					trackCTAClick(
						'complete-reservation',
						'Complete Reservation Button Clicked',
						'Complete Reservation',
						click_url
					);

					return props
						.onSubmit(bookingDetails)
						.then((response) =>
							reservationFormHandler(response, props)
						)
						.catch((error) => {
							fetchLocalPhoneNumber(
								city,
								state,
								props.getLocalPhoneNumberByCityState,
								props.setLocalPhone
							);
							throw new SubmissionError({
								_error: error.errors?._error,
							});
						});
				}
			},
	}),
	reduxForm({
		form: 'unit-reservation',
		destroyOnUnmount: false,
		forceUnregisterOnUnmount: true,
		validate,
	})
);

export function UnitReservationForm({
	error,
	unit,
	today,
	handleSubmit,
	invalid,
	submitting,
	botErrors,
	recaptchaSiteKey,
	closures,
	facility,
	localPhone,
}) {
	// A/A test: bucket users for reporting verification (no UI change)
	useDecision(OPTIMIZELY_FLAGS.UNIT_RESERVATION_AA_TEST.key);

	const handleSubmitButton = (e) => {
		e.preventDefault();

		handleSubmit(); // call the submit handler
	};

	const {
		showFeeWarning,
		url: { city },
	} = facility;

	return (
		<div className="unit-reservation-form">
			<Form
				onSubmit={handleSubmit}
				invalid={invalid}
				successLabel={`success-web-${unit.unitType}-${unit.width}x${unit.length}`}
				failureLabel={`fail-web-${unit.unitType}-${unit.width}x${unit.length}`}
				id="unit-reservation-form"
			>
				<FormGroup>
					<FormItem>
						<Field
							name="firstName"
							label="First name"
							component={TextInput}
							errorMessage="Please provide your first name"
						/>
					</FormItem>
					<FormItem>
						<Field
							name="lastName"
							label="Last name"
							component={TextInput}
							errorMessage="Please provide your last name"
						/>
					</FormItem>
				</FormGroup>
				<FormGroup>
					<FormItem>
						<Field
							name="email"
							label="Email address"
							type="email"
							component={TextInput}
							errorMessage="Please provide a valid email address"
						/>
					</FormItem>
					<FormItem>
						<Field
							name="phone"
							label="Phone number"
							component={TextInput}
							errorMessage="Please provide a valid phone number"
						/>
					</FormItem>
				</FormGroup>
				<FormGroup className="datepicker-credit-card-row">
					<FormItem className="move-in-date">
						<Field
							name="moveInDate"
							label="Move-in date"
							reservationWindow={unit.reservationWindow}
							today={today}
							closures={closures}
							component={UnitDropdownDatepicker}
							errorMessage="Please provide a move-in date"
						/>
						<Text>
							If you're unsure of your move-in date, select an
							estimated date. This can be changed later.
						</Text>
					</FormItem>
				</FormGroup>
				<FieldArray
					name="alternateNames"
					component={AlternateNamesContainer}
				/>

				{showFeeWarning && !!facility?.fees?.totalFees?.value && (
					<UnitReservationFeeDropDown
						fees={facility?.fees}
						price={unit.price}
					/>
				)}

				{!!botErrors && (
					<FormGroup>
						<Field
							name="recaptchaToken"
							sitekey={recaptchaSiteKey}
							component={ReduxCAPTCHA}
						/>
					</FormGroup>
				)}
				{!botErrors && (
					<Field
						name="recaptchaToken"
						type="hidden"
						component="input"
					/>
				)}

				{!!error && !submitting && !botErrors && (
					<div className="main-alert-container">
						<Image
							src={warningIcon}
							alt="warning-icon"
						/>
						<div>
							<Paragraph last>
								{'Your reservation had an error. '}
							</Paragraph>
							<Paragraph last>
								{
									'Please check the information you provided and make a change to resubmit your reservation.'
								}
								{error !== 'ReSubmissionError' && (
									<>
										<br /> &nbsp;&#8226; {error}{' '}
									</>
								)}
							</Paragraph>
						</div>
					</div>
				)}
				<FormGroup>
					<FormItem fill>
						<Button
							type="button"
							onClick={handleSubmitButton}
							disabled={submitting || !!error}
							className="submit-button"
							pink
						>
							{'Complete reservation'}
						</Button>
					</FormItem>
				</FormGroup>
				{!!error && !submitting && !botErrors && (
					<div className="main-alert-container">
						<Paragraph last>
							If you continue to experience errors with this
							reservation, you may need to come back and try again
							at a later time or &nbsp;
							<Link
								href={city}
								rel="noopener noreferrer"
								hideBorder
							>
								try exploring other storage options in the same
								area if you are looking to reserve storage right
								now
							</Link>{' '}
							<span>
								or call{' '}
								<a
									href={`tel:${localPhone?.unformattedNumber}`}
								>
									{localPhone?.number}
								</a>{' '}
								to get help in finding the best storage option
								for you.
							</span>
						</Paragraph>
					</div>
				)}
				{facility.giftCardOffer?.giftCardValue && (
					<GiftCardBanner
						className="reservation-gift-card-banner"
						content={<GiftCardModalLink />}
					/>
				)}

				<FormGroup key="tos">
					<Paragraph
						className="tos-text"
						color="gray"
					>
						No obligation to rent. By clicking to reserve this unit,
						you agree to our
						<Link
							href="/legal/consumer"
							target="_blank"
							rel="noopener noreferrer"
						>
							Terms of Service
						</Link>{' '}
						and
						<Link
							href="/legal/privacy"
							target="_blank"
							rel="noopener noreferrer"
						>
							Privacy Policy
						</Link>
						.
					</Paragraph>
				</FormGroup>
			</Form>
			{!!submitting && (
				<div className="spinner-overlay">
					<Spinner show />
				</div>
			)}
		</div>
	);
}

UnitReservationForm.propTypes = {
	recaptchaSiteKey: PropTypes.string,
	botErrors: PropTypes.bool,
	unit: PropTypes.object.isRequired,
	closures: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
	today: PropTypes.instanceOf(Date),
	submitting: PropTypes.bool,
	handleSubmit: PropTypes.func,
	invalid: PropTypes.bool,
	error: PropTypes.string,
	facility: PropTypes.object.isRequired,
	isScarce: PropTypes.bool.isRequired,
	bookingType: PropTypes.string,
	localPhone: PropTypes.object,
};

// TODO: dont create new moment in defaultProps
UnitReservationForm.defaultProps = {
	today: new Date(),
};

export default enhance(UnitReservationForm);
